import React from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Tooltip from 'eventtia-ui-components/lib/Tooltip';
import Button from 'eventtia-ui-components/lib/Button';
import Typography from '@material-ui/core/Typography';
// import AccessTimeIcon from '@material-ui/icons/AccessTime';
// import PlaceOutlinedIcon from '@material-ui/icons/PlaceOutlined';
import { baseMoment, toAPStyleTime } from '../../helpers/dates';
import { selectEventTimeFormat, selectEventTimezone } from '../../helpers/selectors';
// import ScheduleCheck from '../../assets/scheduleCheckedIcon';
// import ActivityFill from '../../assets/ActivityFilledIcon';

export const SLOT_HEIGHT = 72;
export const SLOT_HEIGHT_MOBILE = 56;
export const BLOCK_SPACING_Y = 6;
export const BLOCK_SPACING_X = 16;
export const BLOCK_WIDTH = 50; // percent
export const DIVIDER_HEIGHT = 1;

export const minutesToPixels = (
  minutes, stepSize, slotHeight = SLOT_HEIGHT
) => Math.round((minutes / stepSize) * slotHeight);

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'absolute',
    paddingLeft: 0,
  },
  fullWidth: {
    width: `calc(72% - ${2 * BLOCK_SPACING_X}px)`,
    [theme.breakpoints.down('sm')]: {
      width: `calc(100% - ${2 * BLOCK_SPACING_X}px)`,
    },
  },
  block: {
    overflow: 'hidden',
    touchAction: 'pan-y',
    fontSize: 14,
    border: 'none',
    textAlign: 'left',
    height: '100%',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    position: 'relative',
    padding: 0,
    borderRadius: 0,
    cursor: 'pointer',
    backgroundColor: theme.palette.background.default,
    // '&:hover': {
    //   backgroundColor: theme.palette.lightGrey.light,
    // },
    transition: theme.transitions.create([
      'background-color',
      'border-color',
      'opacity',
    ], {
      duration: theme.transitions.duration.standard,
    }),
  },
  smallBlock: {
    alignItems: 'center',
  },
  blockDetails: {
    flex: 1,
    overflow: 'hidden',
    padding: theme.spacing(2.5, 3, 1.75),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5, 1, 0.5),
    },
  },
  blockRegisteredBorder: {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  blockRegistedDetails: {
    backgroundColor: theme.palette.background.default,
    flex: 1,
    overflow: 'hidden',
    padding: theme.spacing(2.5, 3, 1.75),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5, 1, 0.5),
    },
  },
  smallBlockDetails: {
    [theme.breakpoints.up('md')]: {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  extendedBlockDetails: {},
  mainLine: {
    fontFamily: 'Fancy Cut Pro',
    fontWeight: 400,
    textTransform: 'capitalize',
    fontSize: 24,
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    lineHeight: '100%',
    letterSpacing: '0.04em',
    color: 'inherit',
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(0.5),
      fontSize: 17,
      width: '100%',
    },
  },
  hourLine: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    gap: 4,
    fontFamily: 'Brilliant Cut Pro',
    fontWeight: 500,
    fontSize: 12,
    alignContent: 'center',
    alignItems: 'baseline',
    color: 'inherit',
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
      width: 'auto',
    },
  },
  secondaryLine: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowX: 'hidden',
    fontFamily: 'Brilliant Cut Pro',
    fontWeight: 400,
    fontSize: 16,
    lineheight: '100%',
    letterSpacing: '0.04em',
    color: 'inherit',
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
    },
  },
  tertiaryLine: {
    overflow: 'hidden',
    color: 'inherit',
  },
  lineSpacing: {
    marginTop: 11,
    [theme.breakpoints.down('sm')]: {
      marginTop: 2,
    },
  },
  registeredTertiaryLine: {
    color: theme.palette.primary.main,
  },
  tertiaryLineEllipsis: {
    '& > div': {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflow: 'hidden',
    },
  },
  summaryLine: {
    color: 'inherit',
    fontWeight: 900,
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
  },
  icon: {
    width: 10,
    height: 10,
  },
  requestsButton: {
    alignSelf: 'center',
    marginTop: theme.spacing(0),
    marginRight: theme.spacing(1.5),
  },
  requestsButtonBigSlot: {
    alignSelf: 'baseline',
    marginTop: theme.spacing(1),
  },
  tooltip: {
    maxWidth: 'none',
  },
  tooltipContent: {
    '& *': {
      color: theme.palette.common.white,
    },
    '& span': {
      whiteSpace: 'pre-wrap',
    },
  },
  time: {
    fontSize: theme.typography.caption.fontSize,
  },
  downloadActivityBigBlock: {
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
  },
  downloadActivitySmallBlock: {
    paddingRight: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
  },
  registrationBadge: {
    fontSize: 12,
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    borderRadius: 15,
    fontFamily: 'Brilliant Cut Pro',
    fontWeight: 700,
    fontStyle: 'bold',
    lineHeight: '100%',
    letterSpacing: 0,
    alignItems: 'center',
    display: 'flex',
    leadingTrim: 'NONE',
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
      right: theme.spacing(1),
      bottom: theme.spacing(0.5),
      // padding: theme.spacing(0.25, 0.875),
    },
  },
  registeredIcon: {
    width: 24,
    height: 'auto',
    paddingRight: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      width: 20,
    },
  },
  registrationBadgeContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  flagIcon: {
    fontSize: 21,
  },
  location: {
    fontSize: 12,
    paddingTop: theme.spacing(0.5),
    display: 'flex',
    gap: 4,
    fontFamily: 'Brilliant Cut Pro',
    fontWeight: 500,
    alignContent: 'center',
    alignItems: 'baseline',
    color: 'inherit',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
    },
  },
  optional: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(3.75),
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(1.375),
    },
  },
  secondaryLineMobile: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
}));

const THREE_LINES_THRESHOLD = 96;
const TWO_LINES_THRESHOLD = 76;

const Block = ({
  offset, stepSize, fullWidthBlocks,
  startDate, endDate, scheduleStart,
  onClick, disabled,
  className,
  // location, hourLine, description,
  secondaryLine, tertiaryLine,
  tooltipTitle, alternativeText,
  downloadActivity, seeRequests, registered,
  scheduleWidth,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const slotHeight = isMobile ? SLOT_HEIGHT_MOBILE : SLOT_HEIGHT;
  const timeFormat = useSelector(selectEventTimeFormat());
  const eventTimezone = useSelector(selectEventTimezone);
  const { t } = useTranslation('meeting');

  const minutesFromStart = baseMoment(startDate).diff(scheduleStart, 'minutes');
  const durationInMinutes = baseMoment(endDate).diff(startDate, 'minutes');

  const colWidth = ((scheduleWidth * BLOCK_WIDTH) / 100).toFixed(2);
  const offsetX = `${(colWidth * offset) + ((offset + 1) * BLOCK_SPACING_X)}px`;
  const offsetY = minutesToPixels(minutesFromStart, stepSize, slotHeight)
    + BLOCK_SPACING_Y + DIVIDER_HEIGHT;

  const height = minutesToPixels(durationInMinutes, stepSize, slotHeight)
  - (2 * BLOCK_SPACING_Y) - DIVIDER_HEIGHT;

  let content;

  if (height >= THREE_LINES_THRESHOLD) content = (
    <>
      {tertiaryLine && (
        <Typography component="div" className={clsx(classes.tertiaryLine, classes.lineSpacing, registered && classes.registeredTertiaryLine)}>
          {tertiaryLine}
        </Typography>
      )}
      {secondaryLine && (
        <Typography component="div" className={clsx(classes.secondaryLine, classes.lineSpacing)}>
          {secondaryLine}
        </Typography>
      )}
      {/* <div>
        <Typography component="div" className={clsx(classes.mainLine, classes.hourLine)}>
          <AccessTimeIcon className={classes.icon} />
          {hourLine}
        </Typography>
        {!!location && (
          <Typography className={classes.location}>
            <PlaceOutlinedIcon className={classes.icon} />
            {location}
          </Typography>
        )}
      </div> */}
    </>
  );
  else content = (
    <>
      {tertiaryLine && (
        <Typography component="div" className={clsx(classes.tertiaryLine, classes.tertiaryLineEllipsis, registered && classes.registeredTertiaryLine)}>
          {tertiaryLine}
        </Typography>
      )}
      {secondaryLine && (
        <Typography component="div" className={clsx(classes.secondaryLine, classes.secondaryLineMobile)}>
          {secondaryLine}
        </Typography>
      )}
    </>
  );
  // else if (height >= TWO_LINES_THRESHOLD) content = (
  //   <>
  //     <Typography component="div" className={classes.mainLine}>
  //       {mainLine}
  //     </Typography>
  //     {secondaryLine && (
  //       <Typography component="div" className={classes.secondaryLine}>
  //         {secondaryLine}
  //       </Typography>
  //     )}
  //   </>
  // );
  // else content = (
  //   <Typography component="div" className={classes.summaryLine}>
  //     {summaryLine || mainLine}
  //   </Typography>
  // );

  return (
    <Tooltip
      open={false}
      className={classes.tooltip}
      title={(
        <div className={classes.tooltipContent}>
          {tooltipTitle}
          {alternativeText}
          <Typography className={classes.time}>
            {`${toAPStyleTime(baseMoment.utc(startDate).tz(eventTimezone).format(timeFormat))} - ${toAPStyleTime(baseMoment.utc(endDate).tz(eventTimezone).format(timeFormat))}`}
          </Typography>
        </div>
      )}
    >
      <div
        className={clsx(
          classes.wrapper,
          fullWidthBlocks && classes.fullWidth
        )}
        style={{
          height,
          left: offsetX,
          top: offsetY,
          width: !fullWidthBlocks ? `${colWidth}px` : undefined,
        }}
      >
        <button
          className={clsx(
            classes.block,
            (height < TWO_LINES_THRESHOLD) && classes.smallBlock,
            registered && classes.blockRegisteredBorder,
            className
          )}
          // disabled={disabled} // removed this to prevent tooltip bug
          // onClick={(!disabled && (description || optional)) ? onClick : undefined}
          onClick={(!disabled ? onClick : undefined)}
          type="button"
        >
          <div
            className={clsx(
              registered ? classes.blockRegistedDetails : classes.blockDetails,
              (height < TWO_LINES_THRESHOLD) && classes.smallBlockDetails,
              downloadActivity && classes.extendedBlockDetails
            )}
          >
            {content}
          </div>
          {seeRequests && (
            <Button
              small
              className={clsx(
                classes.requestsButton,
                height >= TWO_LINES_THRESHOLD && classes.requestsButtonBigSlot
              )}
              variant="secondary"
              onClick={onClick}
            >
                {t('requests.seeRequests')}
            </Button>
          )}
          {/* {downloadActivity && (
            <div className={clsx(
              (height >= THREE_LINES_THRESHOLD || height >= TWO_LINES_THRESHOLD)
                ? classes.downloadActivityBigBlock : classes.downloadActivitySmallBlock
            )}
            >
              {downloadActivity}
            </div>
          )} */}
        </button>
      </div>
    </Tooltip>
  );
};

Block.propTypes = {
  // mainLine: PropTypes.node.isRequired,
  // hourLine: PropTypes.node.isRequired,
  secondaryLine: PropTypes.node,
  tertiaryLine: PropTypes.node,
  // summaryLine: PropTypes.node,
  downloadActivity: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  scheduleStart: PropTypes.string.isRequired,
  stepSize: PropTypes.number.isRequired,
  offset: PropTypes.number.isRequired,
  alternativeText: PropTypes.string,
  disabled: PropTypes.bool,
  tooltipTitle: PropTypes.node,
  fullWidthBlocks: PropTypes.bool,
  seeRequests: PropTypes.bool,
  // optional: PropTypes.bool.isRequired,
  registered: PropTypes.bool.isRequired,
  // location: PropTypes.string,
  // description: PropTypes.string,
  scheduleWidth: PropTypes.number.isRequired,
};

Block.defaultProps = {
  secondaryLine: undefined,
  tertiaryLine: undefined,
  // summaryLine: undefined,
  tooltipTitle: undefined,
  alternativeText: undefined,
  disabled: false,
  downloadActivity: undefined,
  fullWidthBlocks: true,
  seeRequests: false,
  className: undefined,
  // location: '',
  // description: '',
};

export default Block;
